import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Calendar, User, ArrowRight, Buildings, Briefcase, Star, FilePdf, Eye, X, DownloadSimple } from '@phosphor-icons/react';
import axios from 'axios';
import { Seo } from '../components/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolvePdfUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${BACKEND_URL}${url}`);

function CaseStudyArticleView({ slug }) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/case-studies/${slug}`)
      .then(r => setArticle(r.data))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <main className="pt-24 bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-lime-600 border-t-transparent mx-auto"></div>
      </div>
    </main>
  );

  if (!article) return (
    <main className="pt-24 bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h1 className="font-outfit font-bold text-2xl text-neutral-900 mb-4">Case Study Not Found</h1>
        <p className="text-neutral-600 mb-6">The case study you&apos;re looking for doesn&apos;t exist.</p>
        <Link to="/case-studies" className="text-lime-600 font-medium hover:underline inline-flex items-center gap-2">
          <ArrowLeft size={18} /> Back to Case Studies
        </Link>
      </div>
    </main>
  );

  return (
    <main className="pt-24 bg-white" data-testid="case-study-article-view">
      {article.image_url && (
        <section className="relative h-64 sm:h-80 lg:h-[28rem] overflow-hidden bg-neutral-100">
          <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/70 via-transparent to-transparent" />
        </section>
      )}
      <section className="py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-lime-600 font-medium hover:text-lime-700 transition-colors mb-8" data-testid="case-studies-back-link">
            <ArrowLeft size={18} weight="bold" /> Back to Case Studies
          </Link>
          <motion.article initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex flex-wrap items-center gap-4 text-neutral-500 text-sm mb-4">
              {article.date && (
                <div className="flex items-center gap-1.5">
                  <Calendar size={16} weight="duotone" className="text-lime-600" />
                  <span>{article.date}</span>
                </div>
              )}
              {article.client && (
                <>
                  <span className="text-neutral-300">|</span>
                  <div className="flex items-center gap-1.5">
                    <Buildings size={16} weight="duotone" className="text-lime-600" />
                    <span>{article.client}</span>
                  </div>
                </>
              )}
              {article.sector && (
                <>
                  <span className="text-neutral-300">|</span>
                  <div className="flex items-center gap-1.5">
                    <Briefcase size={16} weight="duotone" className="text-lime-600" />
                    <span>{article.sector}</span>
                  </div>
                </>
              )}
              {article.author && (
                <>
                  <span className="text-neutral-300">|</span>
                  <div className="flex items-center gap-1.5">
                    <User size={16} weight="duotone" className="text-lime-600" />
                    <span>{article.author}</span>
                  </div>
                </>
              )}
            </div>
            <h1 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight mb-8" data-testid="case-study-article-title">
              {article.title}
            </h1>
            {article.excerpt && (
              <p className="text-lg text-neutral-600 leading-relaxed mb-8 border-l-4 border-lime-500 pl-5 italic">{article.excerpt}</p>
            )}
            {article.content ? (
              <div className="prose prose-neutral prose-lg max-w-none" data-testid="case-study-article-content">
                {article.content.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="text-neutral-700 leading-relaxed mb-5">{paragraph}</p>
                ))}
              </div>
            ) : (
              <p className="text-neutral-600 leading-relaxed">{article.excerpt}</p>
            )}
            {article.pdf_url && (
              <div className="mt-10 rounded-2xl border border-neutral-200 bg-neutral-50 p-5 sm:p-6" data-testid="case-study-pdf-block">
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="p-3 rounded-xl bg-lime-100 text-lime-700 shrink-0">
                    <FilePdf size={28} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-outfit font-semibold text-neutral-900 mb-1">Full Case Study (PDF)</p>
                    <p className="text-sm text-neutral-600 break-words">{article.pdf_filename || 'View or download the full version of this case study.'}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                    <a
                      href={resolvePdfUrl(article.pdf_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-neutral-300 bg-white text-neutral-700 text-sm font-medium hover:border-lime-400 hover:text-lime-600 transition-colors"
                      data-testid="case-study-pdf-view-inline"
                    >
                      <Eye size={16} weight="bold" /> View
                    </a>
                    <a
                      href={resolvePdfUrl(article.pdf_url)}
                      download
                      className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-lime-600 text-white text-sm font-medium hover:bg-lime-700 transition-colors"
                      data-testid="case-study-pdf-download-inline"
                    >
                      <DownloadSimple size={16} weight="bold" /> Download
                    </a>
                  </div>
                </div>
                <iframe
                  src={`${resolvePdfUrl(article.pdf_url)}#view=FitH`}
                  title={article.pdf_filename || 'Case study PDF'}
                  className="hidden md:block w-full h-[520px] bg-white border border-neutral-200 rounded-xl mt-5"
                />
              </div>
            )}
          </motion.article>
          <div className="mt-12 pt-8 border-t border-neutral-200">
            <div className="flex items-center justify-between">
              <Link to="/case-studies" className="inline-flex items-center gap-2 text-lime-600 font-medium hover:text-lime-700 transition-colors">
                <ArrowLeft size={18} weight="bold" /> All Case Studies
              </Link>
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                Get in Touch <ArrowRight size={18} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function PdfPreviewModal({ open, onClose, pdfUrl, title }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="case-study-pdf-modal"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
              <div className="flex items-center gap-2 min-w-0">
                <FilePdf size={22} weight="duotone" className="text-lime-600 shrink-0" />
                <h3 className="font-outfit font-semibold text-neutral-900 truncate">{title || 'Case Study PDF'}</h3>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={pdfUrl}
                  download
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-lime-600 text-white text-sm font-medium hover:bg-lime-700 transition-colors"
                  data-testid="case-study-pdf-download"
                >
                  <DownloadSimple size={16} weight="bold" /> Download
                </a>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-neutral-100 transition-colors"
                  aria-label="Close PDF preview"
                  data-testid="case-study-pdf-modal-close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <iframe
              src={pdfUrl}
              title={title || 'PDF preview'}
              className="flex-1 w-full bg-neutral-100"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PdfHoverButton({ pdfUrl, pdfFilename, title }) {
  const [hovering, setHovering] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [hoverTimer, setHoverTimer] = useState(null);
  const resolved = resolvePdfUrl(pdfUrl);

  const onEnter = () => {
    const t = setTimeout(() => setHovering(true), 350);
    setHoverTimer(t);
  };
  const onLeave = () => {
    if (hoverTimer) clearTimeout(hoverTimer);
    setHovering(false);
  };

  return (
    <>
      <div className="relative inline-block" onMouseEnter={onEnter} onMouseLeave={onLeave}>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setModalOpen(true); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-neutral-900 text-white text-xs font-semibold shadow-sm hover:bg-lime-600 transition-colors"
          data-testid={`case-study-pdf-btn`}
          aria-label="View case study PDF"
        >
          <FilePdf size={13} weight="fill" />
          View PDF
          <Eye size={12} weight="bold" className="opacity-80" />
        </button>
        {/* Desktop hover preview */}
        <AnimatePresence>
          {hovering && (
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              className="hidden md:block absolute z-50 right-0 top-full mt-2 w-[420px] bg-white border border-neutral-200 rounded-xl shadow-2xl overflow-hidden pointer-events-none"
              data-testid="case-study-pdf-hover-preview"
            >
              <div className="px-3 py-2 bg-neutral-900 text-white text-[11px] font-medium flex items-center gap-1.5">
                <FilePdf size={12} weight="fill" className="text-lime-400" />
                <span className="truncate">{pdfFilename || title || 'PDF Preview'}</span>
              </div>
              <iframe
                src={`${resolved}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                title="PDF hover preview"
                className="w-full h-[300px] bg-neutral-100"
                aria-hidden="true"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <PdfPreviewModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        pdfUrl={resolved}
        title={pdfFilename || title}
      />
    </>
  );
}

function CaseStudyCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden animate-pulse">
      <div className="aspect-[16/10] bg-neutral-100" />
      <div className="p-6 space-y-3">
        <div className="h-3 w-1/3 bg-neutral-100 rounded" />
        <div className="h-5 w-4/5 bg-neutral-100 rounded" />
        <div className="h-4 w-full bg-neutral-100 rounded" />
        <div className="h-4 w-2/3 bg-neutral-100 rounded" />
      </div>
    </div>
  );
}

function CaseStudiesGrid() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/case-studies`)
      .then(r => setArticles(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="py-16 lg:py-24 bg-white" data-testid="case-studies-grid">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="case-studies-skeleton">
            {[0, 1, 2].map(i => <CaseStudyCardSkeleton key={i} />)}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-16"><p className="text-neutral-500">No case studies yet.</p></div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <Link
                  to={`/case-studies/${article.slug}`}
                  className="group block bg-white rounded-2xl border border-neutral-200 overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-lime-200"
                  data-testid={`case-study-card-${article.slug}`}
                >
                  {article.image_url && (
                    <div className="aspect-[16/10] overflow-hidden relative">
                      <img src={article.image_url} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      {article.featured && (
                        <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-lime-600 text-white text-[10px] font-semibold shadow-md" data-testid={`featured-badge-${article.slug}`}>
                          <Star size={11} weight="fill" />
                          Featured
                        </div>
                      )}
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {article.client && (
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-lime-100 text-lime-700">
                          {article.client}
                        </span>
                      )}
                      {article.sector && (
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
                          {article.sector}
                        </span>
                      )}
                    </div>
                    <h3 className="font-outfit font-semibold text-lg text-neutral-900 group-hover:text-lime-600 transition-colors mb-2 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-neutral-600 text-sm line-clamp-3">{article.excerpt}</p>
                    <div className="flex items-center justify-between mt-4 gap-2 flex-wrap">
                      {article.date && <span className="text-xs text-neutral-500">{article.date}</span>}
                      <div className="flex items-center gap-2 ml-auto">
                        {article.pdf_url && (
                          <PdfHoverButton pdfUrl={article.pdf_url} pdfFilename={article.pdf_filename} title={article.title} />
                        )}
                        <span className="inline-flex items-center gap-1 text-lime-600 text-sm font-medium group-hover:gap-2 transition-all">
                          Read More <ArrowRight size={14} weight="bold" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function CaseStudiesPage() {
  const { slug } = useParams();

  if (slug) return <CaseStudyArticleView slug={slug} />;

  return (
    <main className="pt-24 bg-white" data-testid="case-studies-page">
      <Seo title="Case Studies" description="Real projects, real partnerships, real results. Explore how NSEC delivers tailored services to businesses, events, public sector clients and our own teams." path="/case-studies" />
      <section className="py-16 lg:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="accent-font text-lime-600 mb-4">Case Studies</p>
            <h1 className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl text-neutral-900 tracking-tight mb-6">
              Our <span className="text-lime-600">Work in Action</span>
            </h1>
            <p className="text-neutral-600 text-lg max-w-3xl">
              Real projects, real partnerships, and real results. Explore how NSEC has delivered
              tailored services to businesses, events, public sector clients, and our own teams.
            </p>
          </motion.div>
        </div>
      </section>
      <CaseStudiesGrid />
    </main>
  );
}
